import { CASE_REVIEW_SECTIONS, type CaseReviewSectionId } from "../constants/caseReviewSections";

/** The generated schema key the catalog's `x_agent` declaration is emitted under, beside `x-field-id`. */
export const AGENT_ACTIONS_SCHEMA_KEY = "x-agent-actions";

/** How a field action reaches Theragenie: as an editable draft in the composer, or sent at once. */
export const FIELD_ACTION_SENDS = { compose: "compose", immediate: "immediate" } as const;

export type FieldActionSend = (typeof FIELD_ACTION_SENDS)[keyof typeof FIELD_ACTION_SENDS];

/** One action the catalog declares on a field, as the generated schema carries it. */
export interface FieldAgentAction {
  id: string;
  label: string;
  icon?: string;
  send: FieldActionSend;
  /** The prompt template; placeholders are expanded by `fieldActionPrompt.ts`. */
  prompt: string;
  /** The context ids the agent is asked to read with its tools (`section.values`, `sections:<ids>`, …). */
  context: string[];
}

/** A field's `x-agent-actions`: its actions, and the field (by full id) a proposal must set together with it. */
export interface FieldAgentActions {
  actions: FieldAgentAction[];
  pair?: string;
}

/**
 * What the button hands the dock: identifiers only, never a field value. The same object is sent
 * to the server as the `Field action the user started` context entry, so it stays small and holds
 * nothing the transcript should not.
 */
export interface FieldActionContext {
  actionId: string;
  fieldId: string;
  fieldLabel: string;
  sectionId: CaseReviewSectionId;
  recordId?: string;
  pairFieldId?: string;
  contextIds: string[];
}

/** The action as the dock receives it: its label and send mode, with the prompt already expanded. */
export interface ComposedFieldAction {
  label: string;
  send: FieldActionSend;
  prompt: string;
}

/** Composes a field action into the dock; registered by the dock while it is mounted. */
export type FieldActionCompose = (context: FieldActionContext, action: ComposedFieldAction) => void;

/** The `x-agent-actions` declaration of a field schema, or undefined for a field without one. */
export function agentActionsOf(schema: unknown): FieldAgentActions | undefined {
  if (typeof schema !== "object" || schema === null) return undefined;
  const declared = (schema as Record<string, unknown>)[AGENT_ACTIONS_SCHEMA_KEY];
  if (declared === undefined) return undefined;
  const actions = (declared as FieldAgentActions).actions;
  if (!Array.isArray(actions) || actions.length === 0) throw new Error(`${AGENT_ACTIONS_SCHEMA_KEY} declares no actions`);
  return declared as FieldAgentActions;
}

/** The section a field id belongs to; every catalog field id starts with its section id. */
export function sectionIdOfField(fieldId: string): CaseReviewSectionId {
  const section = CASE_REVIEW_SECTIONS.find((candidate) => fieldId.startsWith(`${candidate.id}.`));
  if (section === undefined) throw new Error(`${fieldId} is not a field of any Case Review section`);
  return section.id;
}

/** The section's title as the catalog authored it: the `{{section.label}}` of a prompt. */
export function sectionLabelOf(sectionId: CaseReviewSectionId): string {
  const section = CASE_REVIEW_SECTIONS.find((candidate) => candidate.id === sectionId);
  if (section === undefined) throw new Error(`${sectionId} is not a Case Review section`);
  return String(section.fallbackSchema.title ?? sectionId);
}

/** The context for one action of one field, built from identifiers the row already has. */
export function fieldActionContextFor(action: FieldAgentAction, declaration: FieldAgentActions,
                                      field: { fieldId: string; fieldLabel: string; recordId?: string }): FieldActionContext {
  return {
    actionId: action.id,
    fieldId: field.fieldId,
    fieldLabel: field.fieldLabel,
    sectionId: sectionIdOfField(field.fieldId),
    ...(field.recordId === undefined ? {} : { recordId: field.recordId }),
    ...(declaration.pair === undefined ? {} : { pairFieldId: declaration.pair }),
    contextIds: [...action.context],
  };
}
